import { Box, HStack } from '@chakra-ui/react';
import { useTransactionsModel } from '@common/models/TransactionsModel';
import { getMonthNameByMonthNumber } from '@common/utility/datetime';
import * as d3 from 'd3';
import React, { PropsWithChildren, useState } from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { CHART_HEIGHT } from '.';
import { useDashboardModel } from '../../models/DashboardModel';
import { ChartBar } from './ChartBar';
import { createBarsFromDividends } from './functions';

export const Chart: React.FC = () => {
  const model = useDashboardModel();
  const transactionModel = useTransactionsModel();

  // bar calcs
  const weeklyBars = createBarsFromDividends(transactionModel.dividends?.recent_dividends ?? [], 12);
  const barsToRender = weeklyBars;
  const barsToShow = 12;

  const [selected, setSelected] = useState<number>(barsToShow - 1);

  // dividend calcs
  const dividendsBarMaxHeight = CHART_HEIGHT * 0.8; // 80% of chart CHART_HEIGHT
  const dividendsBarMinHeight = CHART_HEIGHT * 0.2; // at least 20% of chart CHART_HEIGHT
  const dividends = barsToRender.map((bar) => Number(bar?.dividend?.amount) ?? 0);
  const dividendMin = d3.min(dividends, (d: number) => d ?? 0) as number;
  const dividendDomain = d3.extent(dividends, (d: number) => d || dividendMin) as number[];
  const dividendRange = [dividendsBarMinHeight, dividendsBarMaxHeight];
  const dividendsBarScaling = d3.scaleLinear().domain(dividendDomain).range(dividendRange);

  // reward calcs
  const rewardsBarMaxHeight = CHART_HEIGHT * 0.2;
  const rewardsBarMinHeight = 0;
  const rewards = barsToRender.map((bar) => Number(bar?.dividend?.rewards_applied) ?? 0);
  const rewardMin = d3.min(rewards, (d: number) => d ?? 0) as number;
  const rewardDomain = d3.extent(rewards, (d: number) => d ?? rewardMin) as number[];
  const rewardRange = [rewardsBarMinHeight, rewardsBarMaxHeight];
  const rewardsBarScaling = d3.scaleLinear().domain(rewardDomain).range(rewardRange);
  const WEEKLY_BAR_WIDTH_GAP_RATIO = 0.3;

  // bar sizing calcs
  const windowWidth = Math.min(window.innerWidth - 48, 500);
  const ratio = WEEKLY_BAR_WIDTH_GAP_RATIO;
  const barWidth = `${(windowWidth / barsToShow) * ratio}px`;
  const borderRadius = (windowWidth / barsToShow) * ratio;

  const moneyEarned = barsToRender[selected].dividend?.amount;

  // bar wrapper
  const BarContainer: React.FC<PropsWithChildren> = ({ children }) => (
    <Box alignItems="center" width={barWidth}>
      {children}
    </Box>
  );

  return (
    <Box h={'220px'} w="100%" onClick={() => model.setSelectedBar(null)}>
      <HStack mb="40px" justifyContent="space-between" pl="30px" pr="14px" pt="24px" pb="10px" alignItems="flex-start">
        <TextBlock mt="3px" weight="bold" size="20px" color="black">
          Money Earned
        </TextBlock>
        <Box display="flex" flexDirection="column">
          <Box display="flex" gap="0px" backgroundColor="white" px="15px" py={'5px'} borderRadius="8px">
            {moneyEarned == null ? (
              <TextBlock weight="bold">{`Nothing earned yet`}</TextBlock>
            ) : (
              <>
                <TextBlock weight="bold">{`$${moneyEarned?.slice(0, moneyEarned.indexOf('.') + 4)}`}</TextBlock>
                <TextBlock ml={'1px'} weight="bold" color="gray.200">
                  {`${moneyEarned?.slice(moneyEarned.indexOf('.') + 4)}`}
                </TextBlock>
              </>
            )}
          </Box>
        </Box>
      </HStack>
      <Box
        px={'24px'}
        alignItems="flex-end"
        display="flex"
        flexDirection="row"
        height={CHART_HEIGHT}
        justifyContent="space-between"
        width="100%">
        {barsToRender.map((bar, i) => {
          return (
            <Box
              key={`bar-${i}`}
              onClick={() => setSelected(i)}
              h={CHART_HEIGHT}
              cursor="pointer"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-end"
              w={barWidth}>
              {bar?.dividend ? (
                <BarContainer key={`bar-${i}`}>
                  <ChartBar
                    selected={i === selected}
                    last={i === barsToRender.length - 1}
                    bar={bar}
                    borderRadius={borderRadius}
                    dividendHeight={dividendsBarScaling(Number(bar.dividend?.amount ?? '0'))}
                    rewardHeight={rewardsBarScaling(Number(bar.dividend?.rewards_applied ?? '0'))}
                    width={barWidth}
                  />
                </BarContainer>
              ) : (
                <BarContainer key={`empty-bar-${i}`}>
                  <Box w="100%" h="2px" backgroundColor={i === selected ? 'gray.600' : 'gray.200'}></Box>
                </BarContainer>
              )}
              <Box display="flex" justifyContent="center" minH="18px" w={'28px'} my="10px">
                {bar.showMonth && (
                  <TextBlock fontSize="12px" weight="bold" textTransform="uppercase">
                    {getMonthNameByMonthNumber(bar.date.getMonth(), 'abbr')}
                  </TextBlock>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
